// cspell:ignore Winback
import type { MeterData } from "@product/rosetta-sdk";
import rosettaSdk, { TinypassSDK } from "@product/rosetta-sdk";
import capitalize from "lodash/capitalize";
import { useCallback, useEffect, useRef } from "react";
import { useRelayEnvironment } from "react-relay";
import { safeSetLocalStorage } from "scmp-sdk";

import { getGoogleLoginUrl } from "shared/lib/account";

import { useArticlePianoPaywallContext } from "scmp-app/components/article/article-piano-paywall/contexts";
import { useLoginDialogStateHelper } from "scmp-app/components/login-dialog/hooks";
import {
  sendGA4Tracking,
  sendGA4UserPropertiesTracking,
} from "scmp-app/components/tracking/google-analytics-4/apis";
import { sendGTMSubscribeVariables } from "scmp-app/components/tracking/google-tag-manager/apis";
import type { UntypedGA4Event } from "scmp-app/components/tracking/google-tag-manager/types";
import { useRosettaContext } from "scmp-app/lib/rosetta";
import { eventEmitter } from "scmp-app/lib/rosetta/event-emitter";
import { searchArticles } from "scmp-app/lib/rosetta/helpers";
import {
  MaxReadableArticleCountPerLoggedInUser,
  PaywallMeterViewsLeftLocalstorageKey,
  PaywallMeterViewsLocalstorageKey,
  RegiWallLoginMethodLocalstorageKey,
} from "scmp-app/lib/rosetta/hooks/rosetta-event-handlers/consts";
import { useRosettaSetState } from "scmp-app/lib/rosetta/hooks/rosetta-set-state";

export const useRosettaEventHandlers = () => {
  const environment = useRelayEnvironment();
  const { abandonedCartFlow } = useRosettaContext();
  const { openLoginDialog } = useLoginDialogStateHelper();
  const { handleUpdateRosettaState } = useRosettaSetState();
  const { updateArticlePianoPaywallStateState } = useArticlePianoPaywallContext();
  const latestReferences = useRef({ abandonedCartFlow });
  useEffect(() => {
    latestReferences.current = {
      abandonedCartFlow,
    };
  }, [abandonedCartFlow]);

  const attachEventHandlers = useCallback(() => {
    // TODO: add the event handlers after https://scmp-product-tech.atlassian.net/browse/SCMPWEB-2651 clean up the code

    const handleUpdateRosettaMeterData = (meterData: MeterData) => {
      const { maxViews, views, viewsLeft } = meterData;
      // cspell: ignore regi
      // ignore max = 1 (regi wall) and max = 50 (ad block)
      if ([1, 50].includes(maxViews)) return;

      handleUpdateRosettaState({ meterData });
      safeSetLocalStorage(PaywallMeterViewsLocalstorageKey, `${views}`);
      safeSetLocalStorage(PaywallMeterViewsLeftLocalstorageKey, `${viewsLeft}`);
    };

    rosettaSdk.on("experienceExecute", async context => {
      const handleIPWhitelistGTMVariables = () => {
        const isIPWhiteListCases =
          TinypassSDK.loadedInstance?.customVariables?.ip_access_user &&
          TinypassSDK.loadedInstance?.customVariables?.ip_access_client;
        if (!isIPWhiteListCases) return;

        sendGTMSubscribeVariables({
          ipClientName: TinypassSDK.loadedInstance?.customVariables?.ip_access_client,
        });

        sendGA4UserPropertiesTracking({
          product: "SCMP - IP whitelisting",
        });

        if (TinypassSDK.loadedInstance?.customVariables?.ip_ezproxy_user)
          sendGA4UserPropertiesTracking({ ipClientIsEZproxy: "EZproxy" });
        else sendGA4UserPropertiesTracking({ ipClientIsEZproxy: "IP whitelisting" });
      };

      handleIPWhitelistGTMVariables();

      let paywallState = "paywallNotRequired";
      const requiresInlinePaywall = context.result?.events?.find?.(event => {
        if (event.eventType !== "showTemplate" && event.eventType !== "showOffer") return false;
        return event.eventParams.displayMode === "inline";
      });
      if (requiresInlinePaywall) paywallState = "requiresInlinePaywall";

      const requiresModalPaywall = context.result?.events?.find?.(event => {
        if (event.eventType !== "showTemplate" && event.eventType !== "showOffer") return false;
        return event.eventParams.displayMode === "modal";
      });
      if (requiresModalPaywall) paywallState = "requiresModalPaywall";

      const hasIpAccess = computeHasIpAccess();
      rosettaSdk.rulesEngine.addFact("hasIpAccess", hasIpAccess);
      rosettaSdk.rulesEngine.setReady("pianoExperience");
      await rosettaSdk.rulesEngine.run();

      const hasScmpLiteAccessRight = rosettaSdk?.rulesEngine.getOutcome(
        "user.hasSCMPLiteAccessRight",
      );
      const hasScmpDigitalAccessRight = rosettaSdk?.rulesEngine.getOutcome(
        "user.hasSCMPDigitalAccessRight",
      );
      const hasScmpPlusAccessRight = rosettaSdk?.rulesEngine.getOutcome(
        "user.hasSCMPPlusAccessRight",
      );
      handleUpdateRosettaState({
        hasIpAccess,
        hasPianoExperienceExecuted: true,
        hasScmpDigitalAccessRight,
        hasScmpLiteAccessRight,
        hasScmpPlusAccessRight,
      });
      eventEmitter.emit("showPaywall", paywallState);

      // The window.tp variable is populated after running rosettaSdk.execute
      function computeHasIpAccess() {
        return (
          ((typeof window !== "undefined" &&
            TinypassSDK.loadedInstance?.customVariables?.ip_access_user) ??
            false) ||
          Object.values(rosettaSdk.rulesEngine.getFact("ipRangeAccess") ?? {}).some(Boolean)
        );
      }

      // GA4 user properties
      const { ipAccess, ipRangeContractIds, siteLicenseContractIds } =
        rosettaSdk.rulesEngine.getOutcome("tracking.ga4") ?? {};
      if (siteLicenseContractIds || ipRangeContractIds) {
        const contractIds = [siteLicenseContractIds, ipRangeContractIds]
          .filter(id => id !== null)
          .join(",");
        sendGA4UserPropertiesTracking({ ga4SiteLicensing: contractIds });
      }

      const ipAccessClientId = ipAccess?.id;
      const ipAccessClientType = ipAccess?.type;
      if (ipAccessClientId && ipAccessClientType) {
        switch (ipAccessClientType) {
          case "ezproxy":
            sendGA4UserPropertiesTracking({ ga4Ezproxy: ipAccessClientId });
            break;
          case "whitelisting":
            sendGA4UserPropertiesTracking({ ga4IpWhitelisting: ipAccessClientId });
            break;
        }
      }
    });

    rosettaSdk.on("showOffer", offerParameters => {
      const handleHardPaywallTrackingAndUpdateData = () => {
        if (!offerParameters.activeMeters) return;
        const activeMeters = JSON.parse(offerParameters.activeMeters) as MeterData[];
        if (!activeMeters || activeMeters.length === 0) return;

        const [meterData] = activeMeters; // Assume it has one meter
        const { maxViews, totalViews, viewsLeft } = meterData;
        const isHardPaywall =
          totalViews > MaxReadableArticleCountPerLoggedInUser &&
          maxViews === MaxReadableArticleCountPerLoggedInUser &&
          viewsLeft === 0;

        if (!isHardPaywall) return;

        handleUpdateRosettaMeterData(meterData);
      };

      handleHardPaywallTrackingAndUpdateData();
    });

    rosettaSdk.on("meterActive", meterData => {
      handleUpdateRosettaMeterData(meterData);
    });

    rosettaSdk.registerChannelListener("pwa/request-from-template");
    rosettaSdk.on("COMM_CHANNEL", async event => {
      const { message, ...messageParameters } = event;
      if (!message.requestType) return;

      if (
        message.requestType.startsWith("login/") &&
        !message.requestType.startsWith("login/google/")
      ) {
        const loginDialogConfigs: Record<string, Partial<Parameters<typeof openLoginDialog>[0]>> = {
          "login/archive": {
            ga4CustomParameter: {
              paywall_type: "archive_wall",
              trigger_point: "paywall",
            },
            wallType: "archive",
          },
          "login/churnedHardPaywall": {
            ga4CustomParameter: { paywall_type: "churned hard paywall", trigger_point: "paywall" },
            wallType: "paywall",
          },
          "login/exclusiveHardPaywall": {
            ga4CustomParameter: {
              paywall_type: "exclusive_wall",
              trigger_point: "paywall",
            },
            wallType: "exclusiveWall",
          },
          "login/freeTrialPromo": {
            ga4CustomParameter: {
              trigger_point: "paywall",
            },
            wallType: "freeTrialPromo",
          },
          "login/lite": {
            ga4CustomParameter: {
              paywall_type: "lite_wall",
              trigger_point: "paywall",
            },
            wallType: "lite",
          },
          "login/liteUpgrade": {
            ga4CustomParameter: { paywall_type: "lite_wall", trigger_point: "paywall" },
            wallType: "paywall",
          },
          "login/ltsWall": {
            ga4CustomParameter: { paywall_type: "lts wall", trigger_point: "paywall" },
            wallType: "paywall",
          },
          "login/meter2": {
            ga4CustomParameter: {
              paywall_type: "meter_wall_2",
              trigger_point: "paywall",
            },
            wallType: "meter2",
          },
          "login/meter4": {
            ga4CustomParameter: {
              paywall_type: "meter_wall_4",
              trigger_point: "paywall",
            },
            wallType: "meter4",
          },
          "login/newsletter": {
            ga4CustomParameter: {
              paywall_type: "newsletter_wall",
              trigger_point: "paywall",
            },
            wallType: "newsletter",
          },
          "login/paywall": {
            ga4CustomParameter: {
              paywall_type: "paywall",
              trigger_point: "paywall",
            },
            wallType: "paywall",
          },
          "login/plusPaywall": {
            ga4CustomParameter: {
              paywall_type: "plus_partial_wall",
              trigger_point: "plus partial wall",
            },
            wallType: "plusPaywall",
          },
          "login/regiwall": {
            description: "To continue reading, create your free account or log in now",
            ga4CustomParameter: {
              paywall_type: "sign in wall",
              trigger_point: "paywall",
            },
            wallType: "regi",
          },
          "login/velocity": {
            ga4CustomParameter: {
              paywall_type: "velocity_wall",
              trigger_point: "paywall",
            },
            wallType: "velocity",
          },
        };
        const config = loginDialogConfigs[message.requestType];
        if (!config) {
          console.warn(`Missing login dialog config for ${message.requestType}`);
        }
        const resolvedConfig = config ?? {
          ga4CustomParameter: {
            paywall_type: "paywall",
            trigger_point: "paywall",
          },
          wallType: "paywall",
        };
        openLoginDialog({
          ...resolvedConfig,
          destination: window.location.origin + window.location.pathname,
        });

        rosettaSdk.sendMessage("pwa/response-from-app", {
          ...messageParameters,
          message,
        });
      }

      switch (message.requestType) {
        case "action/paywallCheckout":
          const { term } = message?.requestData || {};
          if (!term) break;
          latestReferences?.current?.abandonedCartFlow?.eventBus.emit({
            data: {
              term: {
                name: term.name,
              },
            },
            name: "SubscribeClicked",
          });
          break;
        case "action/showPaywall":
          eventEmitter.emit("showPaywall", "requiresInlinePaywall");
          message.responseData = "ready";
          break;
        case "action/trackGA4":
          // Skip the type checking at here
          sendGA4Tracking(message.requestData as UntypedGA4Event, { untyped: true });
          break;
        case "appConfig/rosetta":
        case "context/appConfig":
          const config = rosettaSdk.getRosettaAppConfig();
          if (!config) break;
          message.responseData = config;
          break;
        case "article/search":
          const relatedArticles = await searchArticles({
            environment,
            filter: {
              sectionIds: message.requestData?.articleSectionIds ?? [],
            },
          });
          message.responseData = relatedArticles;
          break;
        case "context/subscriptionStatus": {
          const outcome = rosettaSdk.rulesEngine.getOutcome("user");
          if (!outcome) break;
          message.responseData = outcome;
          break;
        }
        case "localStorage/get":
          const localStorageGetVariable = message.requestData?.localStorage;
          if (localStorageGetVariable?.includes("rSDK_")) {
            const localStorageResult = localStorage.getItem(localStorageGetVariable);
            message.responseData = {
              localStorageResult,
              localStorageVar: localStorageGetVariable,
            };
          }
          break;
        case "localStorage/set":
          const localStorageSetVariable = message.requestData?.localStorage;
          const localStorageValue = message.requestData?.localStorageVal;
          if (localStorageSetVariable?.includes("rSDK_") && localStorageValue) {
            safeSetLocalStorage(localStorageSetVariable, JSON.stringify(localStorageValue));
            message.responseData = {
              localStorageResult: localStorageValue,
              localStorageVar: localStorageSetVariable,
            };
          }
          break;
        case "login/google/regiwall":
          safeSetLocalStorage(RegiWallLoginMethodLocalstorageKey, "Google");
          location.href = getGoogleLoginUrl({
            registerSource: "loginwall",
          });
          break;
        case "rendered/paywall":
          const articleEntityId = message.requestData.articleEntityId;
          const paywallType = message.requestData.paywallType;
          if (articleEntityId) {
            updateArticlePianoPaywallStateState?.({
              articleEntityId,
              state: `shown${capitalize(paywallType)}Paywall`,
            });
          }
          break;
        case "rule/facts":
          message.responseData =
            message.requestData.name === "*"
              ? rosettaSdk.rulesEngine.getAllFacts()
              : rosettaSdk.rulesEngine.getFact(message.requestData.name);
          break;
        case "rule/outcome":
          message.responseData =
            message.requestData.name === "*"
              ? rosettaSdk.rulesEngine.getAllOutcome()
              : (rosettaSdk.rulesEngine.getOutcome(message.requestData.name) ?? {});
          break;
        case "tp/customVariables":
          message.responseData = TinypassSDK.loadedInstance?.customVariables ?? {};
          break;
        default:
          return;
      }

      rosettaSdk.sendMessage("pwa/response-from-app", {
        ...messageParameters,
        message,
      });
    });
  }, [environment, handleUpdateRosettaState, openLoginDialog, updateArticlePianoPaywallStateState]);

  return { attachEventHandlers };
};
